import React, { useState } from 'react';
import {
  Button,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { useLanguageChanger } from '../../hooks/useLanguageChanger';
import './AuthLanguageSwitcher.scss';
import { getFlagUrl } from '../../utils/flagUrl';

const AuthLanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const { availableLanguages, loading, error } = useLanguageContext();
  const { changeLanguage, loadingLanguage } = useLanguageChanger();
  const [isOpen, setIsOpen] = useState(false);

  if (loading) {
    return (
      <div className="auth-language-switcher auth-language-switcher--loading">
        <Skeleton height="40px" width="115px" borderRadius="20px" />
      </div>
    );
  }

  if (error || availableLanguages.length === 0) {
    return null;
  }

  const currentLanguage = availableLanguages.find(
    (lang) => lang.code === i18n.language
  ) || availableLanguages[0];

  return (
    <div
      className={`auth-language-switcher ${isOpen ? 'auth-language-switcher--open' : ''} ${loadingLanguage ? 'auth-language-switcher--loading-language' : ''}`}
      data-testid="auth-language-switcher"
    >
      <Menu
        placement="bottom-end"
        gutter={0}
        strategy="fixed"
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
      >
        <>
          <MenuButton
              as={Button}
              data-testid="auth-language-switcher-trigger"
              className="auth-language-switcher__trigger"
              isLoading={!!loadingLanguage}
              isDisabled={!!loadingLanguage}
            >
              <Flex align="center" width="100%">
                <Flex
                  className="auth-language-switcher__left"
                  align="center"
                  justify="flex-start"
                  gap="8px"
                >
                  <Image
                    src={getFlagUrl(currentLanguage.code, { mapEnToGb: true })}
                    alt={currentLanguage.code}
                    boxSize="20px"
                    borderRadius="full"
                    objectFit="cover"
                  />
                  <Text fontSize="16px" fontWeight={400} color="#000">
                    {currentLanguage.code.toUpperCase()}
                  </Text>
                </Flex>
                <Flex
                  className="auth-language-switcher__right"
                  align="center"
                  justify="center"
                >
                  {isOpen ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="7"
                      viewBox="0 0 13 7"
                      fill="none"
                      className="auth-language-switcher__chevron"
                    >
                      <path
                        d="M11.1172 5.99609L6.0569 1.35609L0.996608 5.99609"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="8"
                      viewBox="0 0 18 8"
                      fill="none"
                      className="auth-language-switcher__chevron"
                    >
                      <path
                        d="M3.66406 1.67969L8.72435 6.31969L13.7846 1.67969"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </Flex>
              </Flex>
          </MenuButton>
          <MenuList
            className="auth-language-switcher__dropdown"
            minW="115px"
            py={0}
            px={0}
            borderRadius="0 0 15px 15px"
            border="1px solid"
            borderColor="#DCDCDC"
            borderTop="none"
            transformOrigin="top right"
            motionProps={{
              initial: { opacity: 0, y: -6, scale: 0.98 },
              animate: { opacity: 1, y: 0, scale: 1 },
              exit: { opacity: 0, y: -6, scale: 0.98 },
              transition: { duration: 0.15, ease: 'easeOut' },
              style: { transformOrigin: 'top right' },
            }}
          >
            {availableLanguages.map((language) => (
              <MenuItem
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                closeOnSelect={true}
                isDisabled={loadingLanguage !== null && loadingLanguage !== language.code}
                _hover={{
                  bg: i18n.language === language.code ? 'alea.500' : 'gray.50',
                }}
                bg={i18n.language === language.code ? 'alea.500' : 'transparent'}
                color={i18n.language === language.code ? 'white' : 'inherit'}
                borderRadius="0"
                py={2}
                pl="10px"
                pr={4}
                height="40px"
                className="auth-language-switcher__item"
              >
                <Flex align="center" gap="8px">
                  <Image
                    src={getFlagUrl(language.code, { mapEnToGb: true })}
                    alt={language.code}
                    boxSize="20px"
                    borderRadius="full"
                    objectFit="cover"
                  />
                  <Text fontSize="16px" fontWeight={400} color={i18n.language === language.code ? 'white' : '#000'}>
                    {language.code.toUpperCase()}
                  </Text>
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </>
      </Menu>
    </div>
  );
};

export default AuthLanguageSwitcher;
